import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, HeartPulse, Award, Users, MapPin } from "lucide-react";
import { IMAGES, CONTACT } from "@/data/classes";
import Seo from "@/components/Seo";

export default function About() {
  return (
    <div>
      <Seo
        title="About"
        path="/about"
        description="Rhythm Check Training delivers hands-on, small-group life-support and safety training in the DFW area. Learn about our approach, values, and instructors."
      />
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="font-mono-label text-[#d90429] mb-4">About Us</div>
            <h1 className="font-display text-5xl md:text-6xl font-black text-[#0a0a0a] leading-none">
              Training that
              <br />
              <span className="text-[#d90429]">respects your time.</span>
            </h1>
            <p className="mt-8 text-lg text-gray-700 leading-relaxed max-w-2xl">
              Rhythm Check Training was built on one principle: certification
              should mean readiness. Every class we run is hands-on, small-group,
              and taught by instructors who&apos;ve used these skills in the real world.
            </p>
          </div>
          <div className="lg:col-span-5">
            <img
              src={IMAGES.medical_team}
              alt="Medical team"
              className="w-full h-96 object-cover rounded-lg border border-gray-200"
            />
          </div>
        </div>
      </section>

      <section className="bg-[#f9fafb] py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-12">
          <div>
            <div className="font-mono-label text-[#d90429] mb-3">Our Mission</div>
            <h2 className="font-display text-3xl md:text-4xl font-black text-[#0a0a0a] leading-tight">
              Prepare the DFW community — professional and public — to act in the
              first minutes of an emergency.
            </h2>
          </div>
          <div>
            <div className="font-mono-label text-[#d90429] mb-3">How We Teach</div>
            <p className="text-gray-700 text-lg leading-relaxed">
              Skills matter more than slides. We keep group sizes small, spend most
              of the class on manikins and scenarios, and never rush the practice
              time that actually builds competence.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="font-mono-label text-gray-500 mb-4">What we stand for</div>
          <h2 className="font-display text-4xl md:text-5xl font-black text-[#0a0a0a]">
            Our values.
          </h2>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: HeartPulse, title: "Hands-on first", body: "Practice over PowerPoint, every single class." },
              { icon: Award, title: "Recognized certifications", body: "Aligned with AHA guidelines and OSHA expectations." },
              { icon: Users, title: "Small groups", body: "We target low instructor-to-student ratios so no one gets skipped." },
              { icon: MapPin, title: "DFW-local", body: "Serving Dallas, Fort Worth, and the whole surrounding metroplex." },
            ].map(({ icon: Icon, title, body }) => (
              <div key={title} className="p-6 border border-gray-200 rounded-lg hover-lift">
                <Icon className="w-8 h-8 text-[#d90429]" />
                <h3 className="mt-4 font-display text-xl font-bold text-[#0a0a0a]">{title}</h3>
                <p className="mt-2 text-gray-600 text-sm leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#0a0a0a] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 flex flex-wrap justify-between items-center gap-6">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-black leading-tight">
              Have questions about our approach?
            </h2>
            <p className="mt-2 text-gray-300">
              Call {CONTACT.phone} or send us a message any time.
            </p>
          </div>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 rounded-md bg-[#d90429] px-6 py-3 text-sm font-bold text-white hover:bg-white hover:text-[#d90429] transition-colors"
            data-testid="about-contact-cta"
          >
            Get in touch <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
